/* PGProfessionalExams.js
   Step 9 — Professional Examinations (renamed from "Additional Tests").
   SOW changes:
   - Dropdown instead of Yes/No radio buttons
   - "None" option clears details
   - If any exam is selected (not None), fire hasProfExam=true to Main.js
     so the Upload step can make Professional Certificates mandatory
   Bug fixed: "Other" was not selectable because examName was set to '' on
   Other selection, making isOther=false. Fix: use separate examType + examOtherName.
   Zero modifications to existing TestScores.js
*/
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import InputRow from './InputRow'

const EMPTY_EXAM = { examType: '', examOtherName: '', score: '', dateTaken: '', notes: '' }

/* Migrate legacy format (examName) → new format (examType / examOtherName) */
const migrateExam = (e) => {
  if (e.examType !== undefined) return e  // already new format
  const legacyName = e.examName || ''
  if (!legacyName || legacyName === 'GRE' || legacyName === 'GMAT') {
    return { ...e, examType: legacyName || 'GRE', examOtherName: '' }
  }
  return { ...e, examType: 'Other', examOtherName: legacyName }
}

export default class PGProfessionalExams extends Component {
  constructor (props) {
    super(props)
    const savedExams = (props.profExams || []).map(migrateExam)
    const hasExam = props.hasProfExam || (savedExams.length > 0 && savedExams[0].examType !== 'None')
    this.state = {
      hasExamSection: hasExam ? 'Yes' : (savedExams.length > 0 ? 'No' : ''),
      exams: hasExam && savedExams.length > 0 ? savedExams : [{ ...EMPTY_EXAM }],
      examTestData: [] // fetched from API: [{Id: 16, value: "GRE"}, {Id: 17, value: "GMAT"}]
    }
    this.publish = this.publish.bind(this)
    this.addExam = this.addExam.bind(this)
    this.removeExam = this.removeExam.bind(this)
    this.updateExam = this.updateExam.bind(this)
  }

  componentDidMount () {
    /* Fetch professional exam types from API (GRE, GMAT, etc.) */
    fetch(`${process.env.REACT_APP_API_URL}/api/EducationHistory/GetProfessionalExamTests`)
      .then(res => res.json())
      .then(res => {
        this.setState({ examTestData: res || [] })
      })
      .catch(() => {})

    this.publish()
  }

  publish (override = {}) {
    const s = { ...this.state, ...override }
    const hasExam = s.hasExamSection === 'Yes'
    /* Normalise to examName format expected by handleSave */
    const normalisedExams = hasExam ? s.exams.map(e => ({
      ...e,
      examName: e.examType === 'Other' ? (e.examOtherName || 'Other') : e.examType
    })) : []
    this.props.collectState('PG_PE', {
      hasProfExam: hasExam,
      profExams: normalisedExams
    })
  }

  addExam () {
    this.setState(prev => ({
      exams: [...prev.exams, { ...EMPTY_EXAM, examType: '' }]
    }), () => this.publish())
  }

  removeExam (index) {
    this.setState(prev => ({
      exams: prev.exams.filter((_, i) => i !== index)
    }), () => this.publish())
  }

  updateExam (index, field, val) {
    this.setState(prev => ({
      exams: prev.exams.map((e, i) => i === index ? { ...e, [field]: val } : e)
    }), () => this.publish())
  }

  render () {
    const { hasExamSection, exams, examTestData } = this.state
    const examTypeOptions = [
      { Id: '', value: '— Select Type —' },
      ...examTestData,
      { Id: 'Other', value: 'Other' }
    ]
    const { error } = this.props

    return (
      <div>
        <p style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
          Have you taken the GRE, GMAT, or any other professional examinations?
        </p>

        <InputRow
          rowName='Professional Examination'
          name='select'
          value={hasExamSection}
          data={[
            { Id: '', value: '— Select —' },
            { Id: 'Yes', value: 'Yes, I have taken a professional examination' },
            { Id: 'No', value: 'No, I have not' }
          ]}
          errMsg={error.hasExamSection || ''}
          onChange={(val) => {
            this.setState({
              hasExamSection: val,
              exams: val === 'Yes'
                ? (exams.length > 0 && exams[0].examType ? exams : [{ ...EMPTY_EXAM, examType: '' }])
                : []
            }, () => this.publish())
          }}
        />

        {hasExamSection === 'Yes' && (
          <div style={{ paddingLeft: 12, borderLeft: '3px solid #6AA8FF', marginTop: 8 }}>
            <p style={{ fontWeight: 'bold', color: '#3168CE', marginBottom: 8 }}>
              Please provide details for each examination:
            </p>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
              Note: Supporting documents for these examinations will be required during the document upload step.
            </p>

            {exams.map((exam, index) => (
              <div
                key={index}
                style={{
                  padding: '10px 12px',
                  background: '#f9f9f9',
                  borderRadius: 6,
                  marginBottom: 10
                }}
              >
                {/* Examination Type dropdown — GRE / GMAT / Other */}
                <InputRow
                  rowName='Examination Type'
                  name='select'
                  value={exam.examType}
                  data={examTypeOptions}
                  required='*'
                  errMsg={error.exams && error.exams[index] ? error.exams[index].examType || '' : ''}
                  onChange={(val) => {
                    /* When switching away from Other, clear the free-text name */
                    const otherName = val === 'Other' ? exam.examOtherName : ''
                    this.updateExam(index, 'examType', val)
                    if (val !== 'Other') {
                      this.updateExam(index, 'examOtherName', '')
                    }
                  }}
                />

                {/* Free-text name — only shown when "Other" is selected */}
                {exam.examType === 'Other' && (
                  <InputRow
                    rowName='Examination Name (specify)'
                    name='text'
                    value={exam.examOtherName}
                    required='*'
                    errMsg={error.exams && error.exams[index] ? error.exams[index].examOtherName || '' : ''}
                    onChange={(val) => this.updateExam(index, 'examOtherName', val)}
                  />
                )}

                <InputRow
                  rowName='Score / Result'
                  name='text'
                  value={exam.score}
                  required='*'
                  errMsg={error.exams && error.exams[index] ? error.exams[index].score || '' : ''}
                  onChange={(val) => {
                    if (val.length <= 8 || val === '') this.updateExam(index, 'score', val)
                  }}
                />

                <InputRow
                  rowName='Date Taken'
                  name='date'
                  value={exam.dateTaken}
                  required='*'
                  errMsg={error.exams && error.exams[index] ? error.exams[index].dateTaken || '' : ''}
                  onChange={(val) => this.updateExam(index, 'dateTaken', val)}
                />

                <InputRow
                  rowName='Additional Notes (optional)'
                  name='text'
                  value={exam.notes}
                  onChange={(val) => this.updateExam(index, 'notes', val)}
                />

                {exams.length > 1 && (
                  <button
                    className='save'
                    style={{ background: '#e74c3c', fontSize: 12, padding: '4px 10px', marginTop: 4 }}
                    onClick={() => this.removeExam(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}

            <button className='save bordered-btn' onClick={this.addExam} style={{ marginBottom: 16 }}>
              + Add Another Examination
            </button>
          </div>
        )}

        <button className='save' onClick={() => { this.props.onSave() }}>Next</button>
      </div>
    )
  }
}

PGProfessionalExams.propTypes = {
  onSave: PropTypes.func.isRequired,
  collectState: PropTypes.func.isRequired,
  error: PropTypes.object,
  profExams: PropTypes.array,
  hasProfExam: PropTypes.bool
}

PGProfessionalExams.defaultProps = {
  error: {},
  profExams: [],
  hasProfExam: false
}
